import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, DialogCancel, EmptyState, Field, FormDialog, FormRow, Spinner } from "@cntrl/ui";
import { useMyProfile, useSetAvatar, useUpdateProfile } from "../../app/hooks/useProfile.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { AvatarEditor } from "../shared/AvatarEditor.js";
import styles from "./PartnerProfileModal.module.scss";

/**
 * A partner's "Edit my profile" (ART-239, product owner 2026-10-09) — their name and their picture, nothing else. The
 * partner has no crew profile to keep: these are the two things the member's band sees of them (the Partner card, the
 * guest list's name, the profile line). The same `/me/profile` + `/me/avatar` routes the full editor uses, opened to
 * the partner realm for exactly this.
 *
 * `PUT /me/profile` is a FULL replace, so the save sends back every stored field it does not show (phone, bio, …)
 * untouched — a partner who once had those (a former crew member) must not lose them by renaming themselves.
 */
export function PartnerProfileModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const q = useMyProfile();
  const me = q.data;
  const update = useUpdateProfile();
  const setAvatar = useSetAvatar(me?.personId ?? undefined);
  const avatarUrl = useAvatarUrl("person", me?.personId, me?.avatarMediaId);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hydrated || !me) return;
    setFirstName(me.firstName ?? "");
    setLastName(me.lastName ?? "");
    setHydrated(true);
  }, [hydrated, me]);

  // The account menu reads the name + picture off /me, not the profile read — refresh it after either change.
  const refreshMe = () => void qc.invalidateQueries({ queryKey: ["me"] });

  async function onSave() {
    if (!me) return;
    setError(null);
    try {
      await update.mutateAsync({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        phone: me.phone ?? null,
        bio: me.bio ?? null,
        location: me.location ?? null,
        positions: me.positions ?? [],
        skills: me.skills ?? [],
      });
      refreshMe();
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Couldn’t save your profile.");
    }
  }

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "?";

  return (
    <FormDialog
      title="Your profile"
      size="sm"
      onClose={onClose}
      footer={
        me && (
          <>
            <DialogCancel />
            <Button
              type="button"
              loading={update.isPending}
              disabled={!firstName.trim() || !lastName.trim()}
              onClick={() => void onSave()}
            >
              Save
            </Button>
          </>
        )
      }
    >
      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={22} label="Loading your profile…" />
        </div>
      ) : q.isError || !me ? (
        <EmptyState
          title="Couldn’t load your profile"
          description="Please try again."
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : (
        <div className={styles.body}>
          <AvatarEditor
            src={avatarUrl}
            placeholder={initials}
            label="Replace profile picture"
            busy={setAvatar.isPending}
            onPicked={(file) =>
              setAvatar.mutate(file, {
                onSuccess: refreshMe,
                onError: (err) =>
                  setError(err instanceof Error && err.message ? err.message : "Couldn’t upload the picture."),
              })
            }
          />
          <FormRow>
            <Field label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <Field label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </FormRow>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </FormDialog>
  );
}
